import type { ParticipantId } from "../types/common";
import type { StoneFlickingCoreConfig } from "../types/game-config";
import type { StoneFlickingCoreState } from "../types/game-state";
import { countRemaining } from "../utils/entity-helpers";
import { getActiveParticipantIds } from "../utils/participant-helpers";

export function evaluateOutcome(
  state: StoneFlickingCoreState,
  config: StoneFlickingCoreConfig,
): StoneFlickingCoreState {
  const activeIds = getActiveParticipantIds(
    state.participants,
    state.eliminatedParticipants,
  );

  const newlyEliminated: ParticipantId[] = [];
  for (const pid of activeIds) {
    const remaining = countRemaining(state.entities, pid);
    if (remaining === 0) {
      newlyEliminated.push(pid);
    }
  }

  const updatedEliminated = [
    ...state.eliminatedParticipants,
    ...newlyEliminated,
  ];

  const stillActive = activeIds.filter((id) => !newlyEliminated.includes(id));

  if (stillActive.length === 1) {
    return {
      ...state,
      phase: "ENDED",
      eliminatedParticipants: updatedEliminated,
      winner: stillActive[0],
      outcomeReason: "ALL_ELIMINATED",
    };
  }

  if (stillActive.length === 0) {
    return {
      ...state,
      phase: "ENDED",
      eliminatedParticipants: updatedEliminated,
      winner: null,
      outcomeReason: "ALL_ELIMINATED",
      isDraw: true,
    };
  }

  if (state.round >= config.maxTurns) {
    const counts = stillActive.map((pid) => ({
      pid,
      count: countRemaining(state.entities, pid),
    }));
    counts.sort((a, b) => b.count - a.count);

    const maxCount = counts[0].count;
    const leaders = counts.filter((c) => c.count === maxCount);

    if (leaders.length === 1) {
      return {
        ...state,
        phase: "ENDED",
        eliminatedParticipants: updatedEliminated,
        winner: leaders[0].pid,
        outcomeReason: "TURN_LIMIT",
      };
    }

    return {
      ...state,
      phase: "ENDED",
      eliminatedParticipants: updatedEliminated,
      winner: null,
      outcomeReason: "TURN_LIMIT",
      isDraw: true,
    };
  }

  if (newlyEliminated.length > 0) {
    return {
      ...state,
      eliminatedParticipants: updatedEliminated,
    };
  }

  return state;
}
